import React, { ChangeEvent, memo, useMemo, useRef } from "react";
import { RCCButton, RCCIcon, RCCDropDown, RCCHookToast, RCCServicesApi, RCCIIconTypes, RCCUseTheme } from "react-common-components";
import { Link } from "react-router-dom";
import { v4 } from "uuid";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import { css } from "styled-components";
import Photo from "src/components/Photo";
import { IMenu } from "src/context/Auth/interface";
import iconScore from "src/assets/imgs/score-icon.png";
import { standardErrorMessages } from "src/utils/errors";
import AppIcons from "src/components/AppIcons";
import getHomePageLink from "src/utils/getHomePageLink";
import { useReloadAnimation } from "src/context/ReloadAnimation/context";
import permissionsMenu, { lastLevelPermissionsMenuSystemConfigs } from "src/context/Auth/permissionsMenu";
import Title from "src/components/Title";
import { ILanguageOptionsAvailable } from "src/context/Translations/interface";
import IRefModalTermsUse from "src/components/ModalTermsUse/interface";
import ModalTermsUse from "src/components/ModalTermsUse";
import { useMaintenance } from "src/context/Maintenance/context";
import { 
	Container, 
	DivRenderMenu, 
	MenuItemChilds, 
	DivItem, 
	MenuTop,
	MenuBottom,
	DivProfilePic,
	MediaQueryImg
} from "./styles";

const ButtonBorderHover = css`
	border: 2px solid transparent;
	&:hover{
		border: 2px solid var(--grey-soft);
	}
`;

const SidebarLinkButton: React.FC<{
	link: string;
	disabled?: boolean;
}> = ({
	link,
	disabled,
	children
}) => {
	if(disabled)
		return <button type="button" disabled>
			{children}
		</button>;
	return <Link to={`/${link}`}>{children}</Link>;
};


const Sidebar: React.FC = () => {
	const { addToast } = RCCHookToast();
	const { translate, changeLanguage, currentLanguage } = useTranslation();
	const { isInMaintenance, toggleMaintenance } = useMaintenance();
	const {reload} = useReloadAnimation();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const { user, defaultDashboard, updateUserImage, homePage, permissions, checkIfHasPermission, checkIfHasContractPermission, superUser, businessGroup, administrator, signOut } = useAuth();
	const { theme, toggleTheme } = RCCUseTheme();
	
	const modalTermsUseRef = useRef<IRefModalTermsUse>(null);

	const changeProfilePic = async (e: ChangeEvent<HTMLInputElement>) => {
		if(!e.target.files)
			return;
			
		if(!["image/jpg", "image/jpeg", "image/png"].includes(e.target.files[0].type)){
			addToast({
				color: "red",
				title: "Erro",
				description: "Você só pode fazer uploads de arquivos jpg, jpeg ou png"
			});
			return;
		}
		
		if(e.target.files[0].size > 150000){
			addToast({
				color: "red",
				title: "Erro",
				description: "Você não pode fazer upload de imagens com tamanho maior que 150 kB"
			});
			return;
		}

			
		try{
			const formData = new FormData();
			formData.append("Files", e.target.files[0]);
			const {data} = await RCCServicesApi.postRequest({
				method: "User/ChangeProfileImage",
				body: formData,
				contentType: "multipart/form-data"
			});

			updateUserImage(data.efa005Imagem);

			addToast({
				color: "green",
				title: translate("Sucesso"),
				description: translate("Imagem do perfil atualizada com sucesso")
			});
		}catch(err){
			standardErrorMessages(err,  "Erro ao trocar imagem do perfil" , addToast);
		}
		
	};

	const changeAndUpdateLanguage = (lng: ILanguageOptionsAvailable) => {
		if(lng === currentLanguage)
			return;
		changeLanguage(lng);
		RCCServicesApi.putRequest({
			method: "User/ChangeLanguage",
			urlParameters: {
				currentLanguage: lng
			}
		});
		reload();
	};

	const recursivelyMountMenu = (item: IMenu, idx: number) => (
		<RCCDropDown
			key={v4()}
			styleInline="width: 100%; display: flex; justify-content: center;"
			action="hover"
			callerComponent={
				<>
					<RCCButton
						color="transparent"
						styleInline={css`
								${ButtonBorderHover}
								padding: 0.25rem;
							`}
					>
						{item.icon && (
							<RCCIcon name={item.icon ? item.icon as RCCIIconTypes : "chart"} size={24} color="var(--grey-soft)"/>
						)}
						{item.img && (
							<AppIcons name={item.img} />
						)}
						<small>
							{translate(item.efb005DescricaoSimplificada ?? item.efb005Descricao)}
						</small>
					</RCCButton>
					{item.children?.find((x) => checkIfHasContractPermission(x.efb001Codigo)) === undefined && (
						<RCCIcon name="diamond" style={{position: "absolute", top: "0", left: "4px"}} color="var(--blue-softer)" size={18} />
					)}
				</>
			}
			position={idx >= 5 && item.children.length >= 8 ? "right" : "rightEnd"}
		>
			<MenuItemChilds bottom={idx >= 5 && item.children.length >= 8} selfEnclose>
				<Title color="defaultColor" bold size="sm" styleInline="margin-left: 8px; margin-top: 8px;">{translate(item.efb005Descricao)}</Title>
				{
					item.children
						.filter((x) => !x.internalAction || (x.internalAction && superUser && businessGroup.efa001Id === 1))
						.map((it) => (
							<DivItem key={v4()}>
								{it.putSeparatorBeforeSidebarItem === true && (
									<>
										<hr />
										<Title color="grey" bold size="xs">
											{translate("Cadastros")}
										</Title>
									</>
								)}
								<SidebarLinkButton
									disabled={!checkIfHasPermission(it.efb001Codigo)}
									link={it.efb001Codigo}
								>
									{!it.internalAction && !checkIfHasContractPermission(it.efb001Codigo) && (
										<RCCIcon name="diamond" size={16} />
									)}
									{translate(it.efb005Descricao)}
								</SidebarLinkButton>
							</DivItem>))
				}
			</MenuItemChilds>

		</RCCDropDown>
	);

	const Menu = useMemo(() => permissionsMenu.filter((x) => x.efb001Codigo !== "systemConfigs").map((item, idx: number) => 
		recursivelyMountMenu(item, idx)
	),[permissionsMenu, permissions]);
    
	return (
		<>
			<Container color={isInMaintenance ? "var(--red-dark)" : ""}>

				<MenuTop>
					{/* Home Page */}
					<RCCButton
						color="transparent"
						styleInline={css`
						border: 2px solid transparent;
						&:hover{
							border: 2px solid #FFF;
						}
					`}
					>
						<Link to={getHomePageLink(homePage, defaultDashboard)}>
							<img className="home-page-icon" src={iconScore} alt="Home Page" />
						</Link>
					</RCCButton>
				</MenuTop>
			
				{/* Menu Itens */}
				<DivRenderMenu>
					{Menu}
				</DivRenderMenu>
			
				<MenuBottom>

				
					{/* User Photo */}
					<RCCDropDown
						action="hover"
						callerComponent={
							<Photo 
								width={50}
								height={50}
								hover={false}
								name={user.efa005Nome}
								link={user.efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${user.efa005Imagem}`}
								styleInline={`${MediaQueryImg} border: none; max-width: 100%; overflow: hidden;`}
							/>
						}
						position="right"
						styleInline="width: 100%; >div{ display: flex; justify-content: center; width: 100%; }"
					>
						<MenuItemChilds bottom>
							<DivProfilePic>
								<Photo 
									width={108}
									height={108}
									hover={false}
									name={user.efa005Nome}
									link={user.efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${user.efa005Imagem}`}
									styleInline="font-size: 2rem; margin: 0.5rem 0;"
								/>
								<h4>{user.efa005Tratamento} <strong>{user.efa005Nome}</strong></h4>
								<label htmlFor="profile-pic">
									<RCCIcon name="camera" />
									<input id="profile-pic" type="file" onChange={changeProfilePic}/>
								</label>
							</DivProfilePic>

							<DivItem>
								<Link to="/myprofile">
									<RCCIcon name="user" />
									{translate("Meu perfil")}
								</Link>
							</DivItem>
							{(superUser || administrator) && (
								<DivItem>
									<Link to="/mybusinessgroup">
										<RCCIcon name="building" />
										{translate("Meu grupo empresarial")}
									</Link>
								</DivItem>
							)}

							<DivItem>
								<button type="button" onClick={toggleTheme} className="buttons-toggle">
									{theme === "dark" ? <RCCIcon name="sun" /> : <RCCIcon name="moon" />}
									{theme === "dark" ? translate("Modo claro") : translate("Modo escuro")}
								</button>
							</DivItem>

							{(user.efa005Id === 1) && (
								<DivItem>
									<button type="button" onClick={toggleMaintenance} className="buttons-toggle">
										<RCCIcon name="exclamationCircle" />
										{isInMaintenance ? translate("Desativar") : translate("Ativar") } {translate("modo manutenção")}
									</button>
								</DivItem>
							)}

							<RCCDropDown
								callerComponent={
									<DivItem>
										<button type="button">
											<RCCIcon name="language" />
											{translate("Idioma")}     
										</button>
										<RCCIcon name="arrowRight" size={28} style={{ position: "absolute", top: 0, right: 0 }} />
									</DivItem>
								}
								position="right"
								styleInline="width: 100%;"
							>
								<MenuItemChilds bottom selfEnclose>
									<DivItem>
										<button type="button" onClick={() => changeAndUpdateLanguage("pt")}>
											{translate("Português")}
										</button>
									</DivItem>
									<DivItem>
										<button type="button" onClick={() => changeAndUpdateLanguage("en")}>
											{translate("Inglês")}
										</button>
									</DivItem>
									<DivItem>
										<button type="button" disabled onClick={() => changeAndUpdateLanguage("es")}>
											{translate("Espanhol")}
										</button>
									</DivItem>
								</MenuItemChilds>
							</RCCDropDown>

							<hr />
							{/* Config */}
							{(administrator || superUser) && (
								<RCCDropDown
									callerComponent={
										<DivItem>
											<button type="button">
												<RCCIcon name="settings" />
												{translate("Configurações")}     
											</button>
											<RCCIcon name="arrowRight" size={28} style={{ position: "absolute", top: 0, right: 0 }} />
										</DivItem>
									}
									position="right"
									styleInline="width: 100%;"
								>
									<MenuItemChilds bottom selfEnclose>
										{lastLevelPermissionsMenuSystemConfigs.filter((x) => !x.internalAction)
											.map((it) => (
												<DivItem key={v4()}>
													<SidebarLinkButton link={it.efb001Codigo}>
														{it.icon && (
															<RCCIcon name={it.icon as RCCIIconTypes} size={12} />
														)}
														{translate(it.efb005Descricao)}
													</SidebarLinkButton>
												</DivItem>
											)
											)}
									</MenuItemChilds>
								</RCCDropDown>
							)}
							{(superUser && businessGroup.efa001Id === 1) && (
								<RCCDropDown
									callerComponent={
										<DivItem>
											<button type="button">
												<RCCIcon name="settings" />
												{translate("Configurações de sistema")}     
											</button>
											<RCCIcon name="arrowRight" size={28} style={{ position: "absolute", top: 0, right: 0 }} />
										</DivItem>
									}
									position="right"
									styleInline="width: 100%;"
								>
									<MenuItemChilds bottom selfEnclose>
										{lastLevelPermissionsMenuSystemConfigs.filter((x) => x.internalAction)
											.map((it) => (
												<DivItem key={v4()}>
													<SidebarLinkButton link={it.efb001Codigo}>
														{it.icon && (
															<RCCIcon name={it.icon as RCCIIconTypes} size={12} />
														)}
														{translate(it.efb005Descricao)}
													</SidebarLinkButton>
												</DivItem>
											)
											)}
									</MenuItemChilds>
								</RCCDropDown>
							)}

							<RCCDropDown
								callerComponent={
									<DivItem>
										<button type="button">
											<RCCIcon name="question" />
											{translate("Ajuda")}     
										</button>
										<RCCIcon name="arrowRight" size={28} style={{ position: "absolute", top: 0, right: 0 }} />
									</DivItem>
								}
								position="right"
								styleInline="width: 100%;"
							>
								<MenuItemChilds bottom selfEnclose style={{ minWidth: "18rem" }}>
									<DivItem>
										<a href="https://docs.scoreplan.com.br/Manuais/Central/199" target="_blank" rel="noopener noreferrer">
											<RCCIcon name="question" />
											{translate("Central de ajuda")} 
										</a>
									</DivItem>
									<DivItem>
										<a href="https://academy.scoreplan.com.br/" target="_blank" rel="noopener noreferrer" >
											<RCCIcon name="graduationCap" />
											Scoreplan Academy
										</a>
									</DivItem>
									<DivItem>
										<a href="https://materiais.scoreplan.com.br/indique-e-ganhe" target="_blank" rel="noopener noreferrer" >
											<RCCIcon name="gift" />
											{translate("Indique e ganhe")} 
										</a>
									</DivItem>
									<DivItem>
										<Link to="/suggestions">
											<RCCIcon name="like" />
											{translate("Sugestões")} 
										</Link>
									</DivItem>
									<DivItem>
										<button type="button" onClick={() => modalTermsUseRef.current?.openModalViewTermsUse()}>
											<RCCIcon name="list"/>
											{translate("Termos de uso")}/{translate("Política de privacidade")} 
										</button>
									</DivItem>								
								</MenuItemChilds>
							</RCCDropDown>

							<hr />
						
							<DivItem>
								<button type="button" onClick={() => signOut()}>
									<RCCIcon name="shutdown" />
									{translate("Sair")}
								</button>
							</DivItem>

						</MenuItemChilds>

					</RCCDropDown>

				</MenuBottom>
			</Container>
			<ModalTermsUse 
				ref={modalTermsUseRef}
			/>
		</>
	);
};

export default memo(Sidebar);
